/**
 * Configured-vs-measured divergence, for the inspector's callout.
 *
 * ── This used to be an inequality; now it is a subtraction ──
 *
 * Until `supply.skuChannels` landed there was no measurement at this grain.
 * The old version compared the declared share against an UPPER BOUND derived
 * from the channel's fleet-wide share — sound only in one direction, and
 * loose enough that it needed a 20% floor, a factor-of-two gap and a 50-
 * request sample floor before it would say anything.
 *
 * `SupplyLink.measured.shareOfSku` is the real number in the real
 * denominator: how much of THIS SKU that leg actually carried. Declared and
 * measured are now directly comparable, so the heuristics that existed to
 * make a bound usable are gone with the bound.
 *
 * What remains is a threshold on the gap, and it is still this page's own
 * judgement rather than a gateway verdict:
 *
 *   `MIN_GAP_PCT = 25` — a quarter of a SKU's first attempts landing
 *   somewhere other than where it was declared. Weighted-random routing plus
 *   retries never drifts that far on its own.
 *
 *   `MIN_SKU_REQUESTS = 50` — a sample floor. Under that volume a share is
 *   noise wearing a percentage sign.
 *
 * BOTH directions are flagged now, which the bound could not do: an active
 * leg declared 10% and carrying 60% is as much a finding as the reverse.
 *
 * ── Reserve rungs are not compared (#2240) ──
 *
 * `shareOfSku` is a leg's DIALS over the SKU's requests, rescued first
 * attempts included (see `MeasuredLeg`). A `reserve` rung declares 0% of the
 * first attempts and, by definition, gets its dials as RETRIES — so its share
 * is not a first-attempt share and cannot disagree with its declaration. The
 * 46.5% `llmport-img` carried on `openai/gpt-image-2` is still on the page:
 * as the ACTIVE `ucloud-images` rung declared 100% and dialled for 48.8%,
 * which is the same gap seen from the side that can be compared. What a
 * reserve rung says in words on its own row ("纯兜底", 44 dialled) is the
 * whole of its finding.
 */

import type { PerspectiveModel, SkuNode, SupplyLink, UpstreamModelNode } from './types.ts';

const MIN_GAP_PCT = 25;
const MIN_SKU_REQUESTS = 50;

export interface DivergenceFinding {
  readonly link: SupplyLink;
  readonly sku: SkuNode;
  readonly target: UpstreamModelNode;
  /** Declared first-attempt share, whole percent of this SKU's pool. */
  readonly configuredPct: number;
  /** MEASURED share of this SKU's traffic, same denominator. Not a bound. */
  readonly measuredPct: number;
}

function findingFor(
  link: SupplyLink,
  sku: SkuNode,
  target: UpstreamModelNode,
): DivergenceFinding | null {
  // A disabled rung declares nothing and should carry nothing; if it carried
  // something that is a different (worse) finding than a share mismatch, and
  // the ladder row says so on its own. A reserve rung's dials are retries,
  // not first attempts, so there is no first-attempt share to compare — see
  // the module header.
  if (link.role !== 'active') return null;
  const leg = link.measured;
  // No measurement at all — unreported group, or a leg that carried nothing.
  // "Declared 100%, carried nothing" is a real finding, but the ladder row
  // already states it in words; this callout is about a share GAP and has
  // nothing to compare against here.
  if (leg === null || leg.dialled === 0) return null;
  if (sku.traffic.requests < MIN_SKU_REQUESTS) return null;
  const measuredPct = leg.shareOfSku * 100;
  if (!Number.isFinite(measuredPct)) return null;
  if (Math.abs(measuredPct - link.configuredSharePct) < MIN_GAP_PCT) return null;
  return { link, sku, target, configuredPct: link.configuredSharePct, measuredPct };
}

/** The widest gap among a set of findings, in either direction. */
function worst(findings: readonly DivergenceFinding[]): DivergenceFinding | null {
  const gap = (finding: DivergenceFinding): number =>
    Math.abs(finding.measuredPct - finding.configuredPct);
  return findings.reduce<DivergenceFinding | null>(
    (best, next) => (best === null || gap(next) > gap(best) ? next : best),
    null,
  );
}

/**
 * The worst divergence involving `node`, looking at the ladder rungs on its
 * side. Returns null when nothing crosses the threshold — which is the common
 * case and must stay silent.
 */
export function findDivergence(
  model: PerspectiveModel,
  node: SkuNode | UpstreamModelNode,
): DivergenceFinding | null {
  const skusById = new Map(model.skus.map((sku) => [sku.id, sku] as const));
  const targetsById = new Map(model.upstreamModels.map((target) => [target.id, target] as const));
  const relevant = model.links.filter((link) =>
    node.kind === 'sku' ? link.fromId === node.id : link.toId === node.id,
  );
  const findings: DivergenceFinding[] = [];
  for (const link of relevant) {
    const sku = skusById.get(link.fromId);
    const target = targetsById.get(link.toId);
    if (sku === undefined || target === undefined) continue;
    const finding = findingFor(link, sku, target);
    if (finding !== null) findings.push(finding);
  }
  return worst(findings);
}
